/**
 * File: apps/qbench-cp/reactapp/src/shared/components/FormV2/Group.js
 *
 * Purpose: Renders a collapsible section wrapper used by FormV2 to group fields that share a
 *     `portalGroupName`.
 *
 * Scope: FormV2 grouped-field section container.
 *
 * Tags: customer-portal
 *
 * Overview: Exports FormGroup, which renders a header (the group name plus a
 *     chevron-down/chevron-right icon indicating collapsed state) and toggles the visibility
 *     of its children via the useDisclosure hook, defaulting to collapsed. The header and
 *     collapse content reuse the AccordionHeader/AccordionCollapse styled elements from the
 *     Order EditTests styles module for a consistent accordion look.
 *
 * Exports: default FormGroup component.
 */

import styled, { useTheme } from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useDisclosure from 'shared/hooks/misc/useDisclosure';
import {
  faChevronDown,
  faChevronRight,
} from '@fortawesome/free-solid-svg-icons';
import {
  AccordionCollapse,
  AccordionHeader,
} from 'Order/SelectTests/EditTests/styles';

const Wrapper = styled.div`
  border-radius: 0.75rem;
  margin: 0 -1.5rem 1.5rem;
  padding: 0.35rem 1.5rem;
  background: ${(p) => p.theme.color.palette.grey[50]};

  ${AccordionCollapse} {
    padding: 1.5rem 1rem 0.5rem;
    background: ${(p) => p.theme.color.palette.grey[50]};
  }

  ${AccordionHeader} {
    font-weight: 700;
    grid: auto / 1fr 40px;
    gap: 0;

    > :first-child {
      place-self: center start;
    }

    > :nth-child(2) {
      place-self: end;
    }

    span {
      width: 100%;
      line-height: 1;
      font-size: 12px;
      letter-spacing: 1px;
      text-transform: uppercase;
      color: ${(p) => p.theme.color.palette.grey[500]};
    }
  }
`;

export default function FormGroup({ name, children }) {
  const theme = useTheme();
  const [isCollapsed, { onToggle }] = useDisclosure(true);
  return (
    <Wrapper>
      <AccordionHeader
        isEmpty={false}
        isCollapsed={isCollapsed}
        onClick={onToggle}
      >
        <span>{name}</span>
        <FontAwesomeIcon
          icon={isCollapsed ? faChevronRight : faChevronDown}
          color={theme.color.palette.grey[500]}
        />
      </AccordionHeader>
      <AccordionCollapse isCollapsed={isCollapsed}>
        {children}
      </AccordionCollapse>
    </Wrapper>
  );
}
